import axios from '@sas/nova-core/axios';
import { getLogger } from '@sas/nova-core/logging';

import { TildeEncode } from '../../../sonder/rest/http/encoding';
import { EffectiveOriginCasAccessControl } from '../../../sonder/rest/representations/casaccessmanagement/cas_access_control';
import {
  MediaTypeAccessControl,
  PermissionReadInfo,
  PermissionTypeGrant,
} from '../../../sonder/rest/representations/casaccessmanagement/constants';

import { DataSourcesProviderCas } from './api';
import { ResolveTableURI } from './resolver_fast';

// eslint-disable-next-line @sas/i18n/jsx-to-localize
const logger = getLogger('DataSelection');

// 105
// CheckAuthorization checks if the current user is authorized for an entity's
// table.
//   - CAS - ReadInfo permission is required
//   - other - no-op for now
export const CheckAuthorization = async (
  tableURI: string,
  tableName: string
): Promise<Error | undefined> => {
  const [tableSummary, err] = ResolveTableURI(tableURI);
  if (err !== undefined || tableSummary === undefined) {
    // if the resourceId is invalid, return the same error as if
    // permission was denied
    return createAuthorizationError(tableName);
  }
  const provider = tableSummary.providerId;
  if (!providerSupportsAuthorization(provider)) {
    // no-op for non-cas
    logger.debug(
      `The authorization check for table ${tableURI} was skipped because the table was provided by ${tableSummary.providerId}, which does not support the auth check.`
    );
    return undefined;
  }
  const server = tableSummary.attributes?.serverId;
  const library = tableSummary.dataSourceId;
  if (!provider || !server || !library) {
    return createAuthorizationError(tableName);
  }

  if (provider === DataSourcesProviderCas) {
    // make sure to tilde encode the path variables
    const casmgmtURI = `/casAccessManagement/servers/${TildeEncode(server)}/caslibs/${TildeEncode(
      library
    )}/tableControls/${TildeEncode(tableName)}/authorization`;

    try {
      const response = await axios({
        method: 'GET',
        url: casmgmtURI,
        params: {
          permissionType: PermissionReadInfo,
          fields: 'type',
        },
        headers: {
          Accept: MediaTypeAccessControl,
        },
      });
      const accessControl = response.data as EffectiveOriginCasAccessControl;

      if (accessControl.type !== PermissionTypeGrant) {
        return createAuthorizationError(tableName);
      }
    } catch (err) {
      return createAuthorizationError(tableName);
    }
  }

  return undefined;
};

// 171
// createAuthorizationError creates an error for a user lacking
// permission for a table.
const createAuthorizationError = (tableName: string): Error => {
  return Error(
    `The item "${tableName}" is not available. It was deleted, moved, or you are not authorized to view the item. Please contact your system administrator for assistance.`
  );
};

// 179
const providerSupportsAuthorization = (provider: string): boolean => {
  return provider === DataSourcesProviderCas;
};
